import { useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Alert from '@mui/material/Alert'
import UndoIcon from '@mui/icons-material/Undo'
import type { Document, Manifest } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { shown, useIntent } from '../api/useIntent'
import { coreIntent } from '../model/manifest'

/**
 * Undo the last change to a component.
 *
 * **It was already possible and nothing offered it.** `component.reverted`
 * is a commandable core intent: a client names the revision to go back
 * to, and the surface resolves that revision's content itself rather
 * than trusting a caller's copy of it — which is why this control can
 * send one number and nothing else, and why a client could not corrupt a
 * document by sending a wrong one.
 *
 * A revert is **a new revision**, not a rewind. The stream keeps every
 * revision that ever happened, including the one being undone, so
 * undoing is itself undoable and nothing is lost by pressing it.
 *
 * **What it deliberately does not offer is a history.** A revision knows
 * when it happened and who did it, and cannot say *what* changed: the
 * intent behind a revision lives on the stream and no read exposes it.
 * "Undo my last change" needs none of that. "Restore what it said on
 * Tuesday" does, and building a revision picker over revision numbers
 * alone would be asking an author to choose between things they cannot
 * tell apart.
 *
 * **Nor does it undo a delete.** Deleting is often more than one command
 * on more than one stream — a record's delete also takes it out of its
 * list and deletes the gallery it owned — and reverting the one stream
 * this button can see brings back the record alone, out of its list and
 * without its photos. Until the platform can say which commands were one
 * act ([B25](../../../docs/blockers.md)), a deleted component offers no
 * undo at all, rather than one that keeps part of its promise.
 */
export function UndoButton({
  tenant,
  document,
  manifest,
  disabled,
}: {
  tenant: string
  document: Document
  manifest: Manifest | undefined
  disabled?: boolean
}) {
  const { send, isLoading: sending, error } = useIntent(tenant)
  const [asking, setAsking] = useState(false)

  // Asked of the manifest rather than assumed, on the same terms every
  // other control in this client is offered: the core's commandable set
  // is published, and a build that withheld revert would get no button
  // rather than a button that fails.
  if (!coreIntent(manifest, 'component.reverted')) return null

  // Revision 1 is the create. There is nothing before it to go back to,
  // and a revert to 0 names a revision that never existed.
  const toRevision = document.revision - 1
  const possible = toRevision >= 1 && !document.tombstoned

  const revert = async () => {
    await send(document.id, 'component.reverted', { toRevision })
    setAsking(false)
  }

  return (
    <>
      <Button
        size="small"
        startIcon={<UndoIcon />}
        disabled={disabled || sending || !possible}
        onClick={() => setAsking(true)}
      >
        Undo the last change
      </Button>

      <Dialog open={asking} onClose={() => setAsking(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Undo the last change?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This puts the content back the way it was one change ago, and records that as a change of its own — so the
            version you are undoing is still there, and undoing is itself undoable.
            <br />
            <br />
            It undoes <strong>everything</strong> the last change touched, not one field of it. Anything edited since
            by somebody else goes back too.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{problemMessage(error, 'The change could not be undone.')}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsking(false)} disabled={sending}>
            Keep it
          </Button>
          <Button variant="contained" onClick={() => revert().catch(shown)} disabled={sending}>
            Undo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
